/**
 * Cryptography service for secure API communication
 * Implements encryption and decryption methods that match the backend implementation
 * 
 * This implementation matches the backend's AES-256-CBC encryption with a 32-byte key
 * and a random initialization vector (IV).
 */
import CryptoJS from 'crypto-js';

const DEFAULT_KEY = '12345678901234567890123456789012';

// Get encryption key from environment variables or use default for development
const KEY = import.meta.env.VITE_ENCRYPTION_KEY || DEFAULT_KEY;
const IV_LENGTH = 16;

/**
 * Encrypt data using AES-256-CBC
 * @param {Object|string} data - Data to encrypt
 * @returns {string} - Encrypted data as base64 string with IV
 */
export const encryptPayload = (data) => {
  const key = CryptoJS.enc.Utf8.parse(KEY);
  const iv = CryptoJS.lib.WordArray.random(IV_LENGTH);

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const result = {
    iv: CryptoJS.enc.Base64.stringify(iv),
    data: encrypted.toString(), // base64
  };

  // Encode like Node.js Buffer.from(...).toString('base64')
  const jsonStr = JSON.stringify(result);
  const base64Final = btoa(jsonStr); // browser-safe Base64

  return base64Final;
};

/**
 * Decrypt data using AES-256-CBC
 * @param {string} encryptedBase64 - Encrypted data as base64 string with IV
 * @returns {Object|string} - Decrypted data
 */
export const decryptPayload = (encryptedBase64) => {
  try {
    if (!encryptedBase64 || typeof encryptedBase64 !== 'string') {
      throw new Error('Invalid input to decryptPayload');
    }

    const jsonStr = atob(encryptedBase64);
    const parsed = JSON.parse(jsonStr);

    const iv = CryptoJS.enc.Base64.parse(parsed.iv);
    const key = CryptoJS.enc.Utf8.parse(KEY);

    const decrypted = CryptoJS.AES.decrypt(parsed.data, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedText);
  } catch (err) {
    console.error('Decryption error:', err.message || err);
    return null;
  }
};

/**
 * Redux Persist transform for encrypting data before storage
 * @param {Object} inboundState - State to be persisted
 * @param {string} key - Key of the state slice
 * @returns {Object} - Encrypted state
 */
export const encryptTransform = {
  in: (inboundState, key) => {
    // Only encrypt sensitive data, not loading/error states
    if (key === 'user' && inboundState) {
      return {
        ...inboundState,
        profile: inboundState.profile ? encryptPayload(inboundState.profile) : null,
        preferences: inboundState.preferences ? encryptPayload(inboundState.preferences) : null
      };
    }
    return inboundState;
  },
  out: (outboundState, key) => {
    // Decrypt sensitive data on rehydration
    if (key === 'user' && outboundState) {
      return {
        ...outboundState,
        profile: outboundState.profile ? decryptPayload(outboundState.profile) : null,
        preferences: outboundState.preferences ? decryptPayload(outboundState.preferences) : null
      };
    }
    return outboundState;
  }
};

/**
 * Redux Persist transform for decrypting data after storage
 * @param {Object} outboundState - State from storage
 * @param {string} key - Key of the state slice
 * @returns {Object} - Decrypted state
 */
export const decryptTransform = {
  in: (inboundState, key) => {
    // Decrypt sensitive data on rehydration
    if (key === 'user' && inboundState) {
      return {
        ...inboundState,
        profile: inboundState.profile ? decryptPayload(inboundState.profile) : null,
        preferences: inboundState.preferences ? decryptPayload(inboundState.preferences) : null
      };
    }
    return inboundState;
  },
  out: (outboundState, key) => {
    // Only encrypt sensitive data, not loading/error states
    if (key === 'user' && outboundState) {
      return {
        ...outboundState,
        profile: outboundState.profile ? encryptPayload(outboundState.profile) : null,
        preferences: outboundState.preferences ? encryptPayload(outboundState.preferences) : null
      };
    }
    return outboundState;
  }
};
